import React, { useState, useMemo, useCallback, useEffect, useRef } from "react";
import "@/App.css";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  FileText,
  Users,
  Receipt,
  Home,
  CalendarClock,
  FileCheck,
  FileSignature,
  Loader2,
  AlertCircle,
  AlertTriangle,
  RefreshCw,
  Settings,
  MessageSquare,
  BookUser,
  BarChart3,
  ClipboardList,
  FolderKanban,
  ClipboardCheck,
  Plus,
  Search,
  Upload,
  Download,
  Calendar,
  CalendarDays,
  Printer,
  UserCheck,
  UserPlus,
  FolderPlus,
  Banknote,
  ReceiptText,
} from "lucide-react";
import { Button } from "@/components/ui/button";

// Feature Components
import Modulistica from "./Modulistica";
import Operatori from "./Operatori";
import ImpostazioniCombinate from "./ImpostazioniCombinate";
import api from "./api";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

// Tab Components
import { 
  DashboardTab, 
  ClientiTab, 
  PraticheTab, 
  ScadenzeTab, 
  FatturazioneTab,
  DocumentiTab
} from "./components/tabs";
import NoteInterneTab from "./components/tabs/NoteInterneTab";
import RubricaClientiTab from "./components/tabs/RubricaClientiTab";
import PraticheQuotidianeTab from "./components/tabs/PraticheQuotidianeTab";
import RegistroAttivitaTab from "./components/tabs/RegistroAttivitaTab";
// RubricaClientiTab is now DiarioGiornalieroTab
import ReportTab from "./components/tabs/ReportTab";
import SafeBoundary from "./components/SafeBoundary";
import SafeModeDiarioGiornaliero from "./components/tabs/SafeModeDiarioGiornaliero";
import SafeModeLavoroQuotidiano from "./components/tabs/SafeModeLavoroQuotidiano";

// Layout Components
import { AppHeader, OperatorWarningBanner, NoOperatorsInfoBanner } from "./components/layout";

// Suite UI Kit — Master Shell v2 (v1.11.0 · Brand Book v1.0)
// Sidebar globale · TopBar · SectionContextBar · Sottomenu · StudioBar · QuickBar.
import {
  SuiteTopBar,
  SectionContextBar,
  SuiteSidebar,
  SuiteStudioBar,
  SuiteQuickBar,
} from "./components/suite";

// Release info (footer tecnico area autenticata — 22/02/2026).
// Riutilizza la stessa config isolata usata dal footer di login.
import { APP_RELEASE } from "./config/appRelease";
// 16/07/2026 — Modularizzazione App.js: estrazione config tab + hook
// centralizzato per i deep-link cross-tab. Riduce l'impronta di App.js
// senza toccare la logica di orchestrazione principale.
import { buildTabsConfig } from "./config/tabsConfig";
import { useDeepLinkListeners } from "./hooks/useDeepLinkListeners";
import usePageScrollSafety from "./hooks/usePageScrollSafety";

// Dialog Components
import { OperatorLinkDialog, ClientDialog, CaseDialog, InvoiceDialog } from "./components/dialogs";
import { PDFPreviewModal } from "./components/dialogs/PDFPreviewModal";
import { MyWorkPanel } from "./components/dialogs/MyWorkPanel";

// Custom Hooks
import { useGestionaleData } from "./hooks/useGestionaleData";
import { useClients, useCases } from "./hooks/useCrudOperations";
import { useInvoices, useDeadlines } from "./hooks/useInvoicesDeadlines";

// Utilities and Constants
import { getDaysUntil, addDays } from "./utils/helpers";
import { deadlineRules } from "./utils/constants";

// Helper: Get deadline urgency level
const getDeadlineUrgency = (dueDate, status) => {
  if (status === "Completata" || status === "completata") return "Completata";
  if (!dueDate) return "Normale";
  
  const days = getDaysUntil(dueDate);
  if (days < 0) return "Scaduta";
  if (days === 0) return "Critica";
  if (days <= 3) return "Urgente";
  if (days <= 7) return "Importante";
  return "Normale";
};

// Document templates for practice types
const documentTemplates = {
  "Assunzione": ["Documento identità", "Codice fiscale", "Curriculum", "Certificati", "Dati contrattuali"],
  "Licenziamento": ["Lettera dimissioni", "Documento identità", "Ultimo cedolino", "TFR"],
  "Pratiche INPS": ["Codice fiscale", "IBAN", "Documento identità", "Modulo domanda"],
  "Contestazione disciplinare": ["Verbale contestazione", "Contratto lavoro", "Testimonianze"],
  "Contenzioso": ["Documentazione completa", "Contratto", "Cedolini", "Corrispondenza"],
  "Consulenza": ["Documentazione caso", "Contratti", "Certificazioni"]
};

export default function GestionaleConsulenteDelLavoro({ onLogout }) {
  // Main data hook - provides all data and loading states
  const {
    isLoading,
    loadingError,
    isSaving,
    setIsSaving,
    clients,
    setClients,
    cases,
    setCases,
    invoices,
    setInvoices,
    deadlines,
    setDeadlines,
    operators,
    setOperators,
    numberingSequences,
    settings,
    currentUser,
    currentOperator,
    currentOperatorId,
    handleLinkOperator,
    getOperatorName,
    activeCases,
    casesToBill,
    readyToBill,
    missingBillingData,
    totalToBill,
    unpaidInvoices,
    totalUnpaid,
    criticalDeadlines,
    upcomingDeadlines,
    loadAllData,
    updateNumberingFromBackend
  } = useGestionaleData();

  // Derive operator role for access control
  const operatorRole = currentUser?.operatorRole || currentOperator?.role || "operatore_agenzia";
  // Both `amministratore` (technical superuser) and `responsabile`
  // (business/office superuser) get full access. Keep this single source
  // of truth so future audit/UI-label divergence is a one-line change.
  const isAdmin = operatorRole === "amministratore" || operatorRole === "responsabile";
  const isStudioOperator = operatorRole === "operatore_studio";
  const isAgenziaOperator = operatorRole === "operatore_agenzia";

  // 16/07/2026 — Config tab estratta in `config/tabsConfig.js` per
  // ridurre l'impronta di App.js. Logica invariata.
  const TABS_CONFIG = useMemo(() => buildTabsConfig(isAdmin), [isAdmin]);

  // ─── Config Tables (30/06/2026) ────────────────────────────────────
  // Tabelle user-managed "Tipo Pratica" + "Ente Coinvolto" caricate UNA
  // SOLA VOLTA all'avvio (no API durante typing — vincolo Performance
  // Engineering Policy). Refetch on-demand dopo edit dalla UI
  // Impostazioni tramite `refreshConfigTables`.
  const [configTables, setConfigTables] = React.useState({
    practice_types: [],
    ente_options: [],
  });
  const refreshConfigTables = React.useCallback(async () => {
    try {
      const data = await api.getConfigTables();
      setConfigTables({
        practice_types: data.practice_types || [],
        ente_options: data.ente_options || [],
      });
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("Config tables load failed:", e?.message || e);
    }
  }, []);
  React.useEffect(() => {
    if (currentUser) refreshConfigTables();
  }, [currentUser, refreshConfigTables]);

  // Apply DEFAULT (soft) management filter based on role.
  // - operatore_studio: pre-impostato a Gestionale Studio.
  // - operatore_agenzia: pre-impostato a Gestionale Agenzia.
  // - amministratore / responsabile: NO implicit filter — see everything.
  // Revisione 20/02/2026: il filtro è ora un DEFAULT collaborativo,
  // NON una barriera rigida. L'operatore può ampliarlo manualmente
  // (Tutti / altra area) per collaborare cross-team.
  React.useEffect(() => {
    if (isStudioOperator) {
      setFilterPracticeType("Gestionale Studio");
    } else if (isAgenziaOperator) {
      setFilterPracticeType("Gestionale Agenzia");
    } else if (isAdmin) {
      // Explicitly reset so a leftover restrictive filter from a prior
      // session/account swap can never linger for an admin/responsabile.
      setFilterPracticeType("all");
    }
  }, [operatorRole, isStudioOperator, isAgenziaOperator, isAdmin]);

  // ── UX Evolution v3 · Fase A · PoC Fatturazione ────────
  // Stato controllato del dialog "Il mio lavoro" quando aperto
  // tramite l'icona della SuiteTopBar (nuovo shell Suite).
  const [suiteMyWorkOpen, setSuiteMyWorkOpen] = React.useState(false);
  // Count preliminare per il badge dell'icona TopBar: contiamo le
  // pratiche assegnate all'operatore (già disponibili senza fetch).
  // Il conteggio completo (scadenze + note) viene mostrato dentro il
  // dialog al momento dell'apertura.
  const suiteMyWorkCount = React.useMemo(() => {
    const opId = currentOperator?.id;
    if (!opId || !Array.isArray(cases)) return 0;
    const CLOSED = new Set(["Chiusa", "Archiviata", "chiusa", "archiviata"]);
    return cases.filter((c) => {
      if (CLOSED.has(c.status)) return false;
      const single = c.assignedOperatorId === opId;
      const multi = Array.isArray(c.assignedOperatorIds) && c.assignedOperatorIds.includes(opId);
      return single || multi;
    }).length;
  }, [cases, currentOperator?.id]);


  // Client CRUD operations
  const {
    isClientDialogOpen,
    setIsClientDialogOpen,
    editingClient,
    clientForm,
    setClientForm,
    handleEditClient,
    handleSaveClient,
    handleDeleteClient,
    resetClientForm
  } = useClients({ clients, setClients, setIsSaving });

  // Case CRUD operations
  const {
    isCaseDialogOpen,
    setIsCaseDialogOpen,
    editingCase,
    caseForm,
    setCaseForm,
    caseHistory,
    handleEditCase,
    handleSaveCase,
    handleDeleteCase,
    handleQuickStatusChange,
    resetCaseForm,
    initNewCaseForm,
    generatePreviewCaseNumberSync,
    fetchNextCaseNumber
  } = useCases({ 
    cases, 
    setCases, 
    clients,
    invoices,
    setDeadlines,
    setIsSaving, 
    currentOperatorId, 
    numberingSequences,
    updateNumberingFromBackend,
    deadlineRules
  });

  // Invoice operations
  const {
    isInvoiceDialogOpen,
    setIsInvoiceDialogOpen,
    invoiceForm,
    setInvoiceForm,
    editingInvoiceId,
    setEditingInvoiceId,
    saveError,
    clearSaveError,
    isPreviewLoading,
    isPreviewModalOpen,
    previewPdfBlob,
    previewError,
    previewFilename,
    closePreviewModal,
    handleSaveInvoice,
    handlePreviewInvoice,
    updateInvoiceStatus,
    createInvoiceFromCase,
    editInvoice,
    duplicateInvoice,
    exportInvoiceXML,
    resetInvoiceForm,
    openNewProforma,
    addInvoicePayment,
    removeInvoicePayment,
    refreshInvoices,
    deleteInvoice
  } = useInvoices({ invoices, setInvoices, cases, setCases, clients, settings });

  // Deadline operations
  const {
    isDeadlineDialogOpen,
    setIsDeadlineDialogOpen,
    editingDeadline,
    deadlineForm,
    setDeadlineForm,
    handleSaveDeadline,
    toggleDeadlineStatus,
    handleDeleteDeadline,
    resetDeadlineForm
  } = useDeadlines({ deadlines, setDeadlines, activeCases });

  // UI State
  const [activeTab, setActiveTab] = useState("dashboard");
  usePageScrollSafety(activeTab);
  const pageScrollRef = useRef(null);
  useEffect(() => {
    if (pageScrollRef.current) pageScrollRef.current.scrollTop = 0;
  }, [activeTab]);

  // ─── Master Shell v2 · Sidebar preference (28/07/2026 · Brand Book v1.0) ───
  // Chiave localStorage per-operatore. Default espansa se preferenza assente.
  // ─── Wave 4.1 (01/07/2026) — Lazy-then-permanent tab mount ─────────
  // Traccia i tab visitati almeno una volta. Ogni TabsContent riceve
  // `forceMount` condizionale: quando il tab è visitato, resta montato
  // permanentemente (nascosto via `hidden` attribute quando inactive).
  // Effetto: prima visita = mount normale (identico al comportamento
  // precedente); riaperture successive = toggle CSS = istantaneo.
  // Preserva stato locale, scroll, filtri e focus tra un tab e l'altro.
  // Cruscotto è già "visitato" al boot (activeTab iniziale).
  const [visitedTabs, setVisitedTabs] = useState(() => new Set(["dashboard"]));
  useEffect(() => {
    if (activeTab && !visitedTabs.has(activeTab)) {
      setVisitedTabs(prev => {
        const next = new Set(prev);
        next.add(activeTab);
        return next;
      });
    }
  }, [activeTab, visitedTabs]);
  // Helper stabile: quando un tab è stato visitato, passiamo
  // `forceMount: true` (Radix rende sempre i children) + `hidden` HTML
  // attribute per nasconderlo quando non attivo. Radix di default con
  // `forceMount` NON nasconde il pannello via `hidden` (comportamento
  // pensato per <Presence> animation), quindi lo gestiamo qui.
  // `hidden` è HTML nativo = `display: none` (nessun impatto su
  // layout/focus/scroll degli altri tab).
  const tabMountProp = useCallback(
    (tabValue) => {
      if (!visitedTabs.has(tabValue)) return {};
      return {
        forceMount: true,
        hidden: activeTab !== tabValue,
      };
    },
    [visitedTabs, activeTab]
  );
  // Quick-action deep-link verso Modulistica (es. "+ Nuovo Preventivo"
  // dal Cruscotto): App.js setta questo flag, Modulistica apre il form
  // corrispondente al mount e poi lo azzera via onInitialFormConsumed.
  const [pendingModulisticaForm, setPendingModulisticaForm] = useState(null);
  // 14/07/2026 — Sub-tab attivo dentro "Gestionale Pratiche".
  // "registro" resta il default all'apertura del modulo principale.
  // Può essere impostato programmaticamente (es. dal Cruscotto per
  // portare l'utente su "preventivi") senza rompere la vista base.
  const [casesSubTab, setCasesSubTab] = useState("registro");
  // Modalità sicura (toggle 22/02/2026) — attivabile via
  // localStorage `safemode=1` o URL `?safemode=1`. Rende i sub-tab
  // di Attività Operative con markup HTML nativo (no Radix Select,
  // no Card, no Dialog, no toast). Serve a isolare la causa root
  // dei crash React 19 `insertBefore`/`removeChild`.
  const [safeMode, setSafeMode] = useState(() => {
    try {
      const url = typeof window !== "undefined" ? new URL(window.location.href) : null;
      if (url?.searchParams.get("safemode") === "1") {
        localStorage.setItem("ui_safemode", "1");
        return true;
      }
      if (url?.searchParams.get("safemode") === "0") {
        localStorage.removeItem("ui_safemode");
        return false;
      }
      return localStorage.getItem("ui_safemode") === "1";
    } catch { return false; }
  });
  const toggleSafeMode = useCallback(() => {
    setSafeMode((v) => {
      const nv = !v;
      try {
        if (nv) localStorage.setItem("ui_safemode", "1");
        else localStorage.removeItem("ui_safemode");
      } catch { /* ignore */ }
      return nv;
    });
  }, []);
  // Deep-link state per il Registro Attività: quando l'utente clicca
  // "Registro Attività" da una scheda cliente, mostriamo direttamente
  // il dettaglio per quel cliente all'apertura della tab.
  const [registroInitialClient, setRegistroInitialClient] = useState({ id: "", name: "" });
  const openRegistroForClient = useCallback((client) => {
    setRegistroInitialClient({ id: client?.id || "", name: client?.name || "" });
    setActiveTab("registro-attivita");
  }, []);
  const [scadenzeDefaultSubTab, setScadenzeDefaultSubTab] = useState(null);
  // Attività Operative parent tab has 2 sub-tabs: Diario + Pratiche Quotidiane.
  // We keep this state in App.js (lifted) so the quick-action buttons above
  // can deep-link directly to either sub-tab and so swapping module/refresh
  // never leaves the inner tab in a stale, half-rendered intermediate state.
  const [activeOperationalTab, setActiveOperationalTab] = useState("diario");

  // Quick-action "pending intent" flag (11/06/2026 — bug fix).
  // The top action bar buttons "Diario Giornaliero" / "Lavoro Quotidiano"
  // must not only navigate to the right sub-tab but ALSO open the
  // creation dialog (parity with Nuova Pratica / Nuovo Cliente /
  // Nuova Fattura / Nuovo Preventivo).
  //
  // The previous implementation dispatched a window CustomEvent via
  // setTimeout(…, 0). Race: the destination sub-tabs are mounted
  // conditionally (`{activeOperationalTab === "diario" && …}`), and
  // React had not yet committed the mount when the event fired →
  // listener missed → dialog stayed closed.
  //
  // Robust fix: lift a `pendingQuickAction` flag in App.js, pass it to
  // the sub-tabs as a prop, and let each tab consume + clear it on
  // mount via useEffect. This guarantees the open is triggered AFTER
  // the listener-side component is actually rendered.
  const [pendingQuickAction, setPendingQuickAction] = useState(null);
  const consumeQuickAction = useCallback((expected) => {
    setPendingQuickAction((curr) => (curr === expected ? null : curr));
  }, []);

  // 11/06/2026 — Cross-component data refresh bus. Componenti che
  // mutano lo stato server-side (es. "Rimuovi da Da Fatturare" che
  // setta billingExcluded sulla pratica) dispatchano "cases:reload"
  // per forzare il refetch globale senza dover passare loadAllData
  // come prop attraverso più livelli di nesting.
  useEffect(() => {
    const onReload = () => { if (loadAllData) loadAllData(); };
    window.addEventListener("cases:reload", onReload);
    return () => window.removeEventListener("cases:reload", onReload);
  }, [loadAllData]);

  const openDiarioTab = useCallback(() => {
    setActiveOperationalTab("diario");
    setActiveTab("attivita");
    setPendingQuickAction("diario:new");
  }, []);
  const openLavoroQuotidianoTab = useCallback(() => {
    setActiveOperationalTab("pratiche");
    setActiveTab("attivita");
    setPendingQuickAction("lavoro:new");
  }, []);

  // Stable callback shared by the operational sub-tabs to open the GLOBAL
  // ClientDialog (registry centralizzato). useCallback prevents a fresh
  // arrow per App render that would otherwise cascade re-renders into
  // the heavy Diario / Pratiche Quotidiane subtrees on every state tick
  // (notification polling, filter typing, etc.).
  // 28/02/2026 — Accetta un opzionale `onCreated(newClient)` callback
  // invocato dal ClientDialog dopo una creazione riuscita, così la
  // tab chiamante (es. Lavoro Quotidiano) può auto-popolare il campo
  // cliente senza ricaricare la pagina. Salvato in ref locale,
  // consumato da ClientDialog tramite `onClientCreated` prop.
  const pendingClientCreatedCb = React.useRef(null);
  const openNewClientDialog = useCallback((onCreated) => {
    pendingClientCreatedCb.current = (typeof onCreated === "function") ? onCreated : null;
    setIsClientDialogOpen(true);
  }, []);

  // Phase 2 — Scadenze Clienti reminders: NotificationBell dispatches
  // `open-scadenze-tab` when the operator clicks "Apri scadenza" on a
  // deadline reminder popup. Route them straight to the Scadenze tab,
  // on the "Clienti" (extra) sub-tab.
  useEffect(() => {
    const onOpenScadenze = () => {
      setScadenzeDefaultSubTab("extra");
      setActiveTab("scadenze");
    };
    window.addEventListener("open-scadenze-tab", onOpenScadenze);
    return () => window.removeEventListener("open-scadenze-tab", onOpenScadenze);
  }, []);

  // ─── Notifiche popup cliccabili (07/07/2026 — intervento urgente) ───
  //
  // Quando l'operatore clicca sul corpo di un popup di notifica,
  // NotificationBell dispatcha un evento custom con tutti i metadati
  // (sourceId, caseId, clientId, kind). Qui lo intercettiamo e
  // impostiamo la tab corretta.
  //
  // Nessun refactor: usiamo `setActiveTab` + `setActiveOperationalTab`
  // già esistenti. Non apriamo dialog né scrolliamo a entry specifici
  // (feature futura — vedi ROADMAP).
  useEffect(() => {
    const onOpenDiaryFromNotif = () => {
      setActiveOperationalTab("diario");
      setActiveTab("attivita");
    };
    const onOpenLavoroFromNotif = () => {
      setActiveOperationalTab("pratiche");
      setActiveTab("attivita");
    };
    const onOpenPracticeFromNotif = (ev) => {
      const caseId = ev?.detail?.caseId || "";
      if (caseId) setSelectedCaseId(caseId);
      setActiveTab("cases");
    };
    const onOpenClientFromNotif = () => {
      setActiveTab("clients");
    };
    window.addEventListener("gi:open-diary-from-notification", onOpenDiaryFromNotif);
    window.addEventListener("gi:open-lavoro-from-notification", onOpenLavoroFromNotif);
    window.addEventListener("gi:open-practice-from-notification", onOpenPracticeFromNotif);
    window.addEventListener("gi:open-client-from-notification", onOpenClientFromNotif);
    return () => {
      window.removeEventListener("gi:open-diary-from-notification", onOpenDiaryFromNotif);
      window.removeEventListener("gi:open-lavoro-from-notification", onOpenLavoroFromNotif);
      window.removeEventListener("gi:open-practice-from-notification", onOpenPracticeFromNotif);
      window.removeEventListener("gi:open-client-from-notification", onOpenClientFromNotif);
    };
  }, []);

  const [searchQuery, setSearchQuery] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  
  // State for opening a specific case directly
  const [selectedCaseId, setSelectedCaseId] = useState(null);
  const [filterPriority, setFilterPriority] = useState("all");
  // Default filterPracticeType is "all": admins and responsabili must
  // see EVERY practice on first load — no implicit Agenzia/Studio gate.
  // The role-aware useEffect below narrows this only for the restricted
  // operator roles (operatore_studio / operatore_agenzia).
  const [filterPracticeType, setFilterPracticeType] = useState("all");
  const [filterManagementType, setFilterManagementType] = useState("all");
  const [filterClient, setFilterClient] = useState("all");
  const [filterBillingStatus, setFilterBillingStatus] = useState("all");
  const [filterDocumentStatus, setFilterDocumentStatus] = useState("all");
  const [isOperatorLinkDialogOpen, setIsOperatorLinkDialogOpen] = useState(false);

  // Navigate to cases with status filter or open single case directly
  const navigateToCasesWithStatus = useCallback((status, casesWithStatus) => {
    if (casesWithStatus.length === 1) {
      // Single case: open it directly via edit
      handleEditCase(casesWithStatus[0]);
      setActiveTab("cases");
    } else if (casesWithStatus.length > 1) {
      // Multiple cases: go to filtered list
      setFilterStatus(status);
      setActiveTab("cases");
    }
    // If 0, do nothing
  }, [handleEditCase]);

  // Track if client dialog was opened from case dialog (for auto-select after creation)
  const [clientDialogFromCase, setClientDialogFromCase] = useState(false);
  // Track if client dialog was opened from invoice dialog (for auto-select after creation)
  const [clientDialogFromInvoice, setClientDialogFromInvoice] = useState(false);

  // Handle client save with auto-select if from case dialog or invoice dialog
  const handleSaveClientWithAutoSelect = useCallback(async () => {
    const savedClient = await handleSaveClient();
    if (savedClient) {
      if (clientDialogFromCase && isCaseDialogOpen) {
        // Auto-select the newly created client in the case form
        setCaseForm(prev => ({ ...prev, clientId: String(savedClient.id) }));
        setClientDialogFromCase(false);
      }
      if (clientDialogFromInvoice && isInvoiceDialogOpen) {
        // Auto-select the newly created client in the invoice form, and
        // mirror the management/lordo defaults that handleClientChange applies.
        // Strict rule: Privato/no-VAT → LORDO, everything else → NETTO.
        const isPrivateNoVat = savedClient.clientType === "Privato"
          || !savedClient.vatNumber
          || String(savedClient.vatNumber).trim() === "";
        setInvoiceForm(prev => ({
          ...prev,
          clientId: String(savedClient.id),
          managementType: savedClient.managementType || prev.managementType || "studio",
          importoLordo: isPrivateNoVat,
          invoiceNumber: "",   // force re-fetch of the right series
          caseId: ""           // reset linked case
        }));
        setClientDialogFromInvoice(false);
      }
      // 28/02/2026 — Generic post-create callback (Lavoro Quotidiano,
      // Diario, Preventivi…). Iniettato da chi ha invocato
      // openNewClientDialog(onCreated). One-shot: viene consumato e
      // resettato.
      if (pendingClientCreatedCb.current) {
        try { pendingClientCreatedCb.current(savedClient); } catch (_) { /* swallow */ }
        pendingClientCreatedCb.current = null;
      }
    }
    return savedClient;
  }, [
    handleSaveClient,
    clientDialogFromCase,
    isCaseDialogOpen,
    setCaseForm,
    clientDialogFromInvoice,
    isInvoiceDialogOpen,
    setInvoiceForm,
  ]);

  // 27/02/2026 — Diario → Crea Pratica/Scadenza completa.
  // Quando l'operatore clicca "Crea Pratica" o "Crea Scadenza" dentro
  // una nota Diario, il componente Diario emette un evento window con
  // i dati pre-compilati. Qui apriamo i dialog veri (CaseDialog per le
  // pratiche, ScadenzeTab per le scadenze clienti), con linkage
  // bidirezionale al backend tramite `sourceModule="diario"`.
  useEffect(() => {
    const onOpenCaseFromDiary = async (ev) => {
      const detail = ev?.detail || {};
      try {
        await initNewCaseForm();
      } catch (e) {
        console.error("initNewCaseForm failed:", e);
      }
      // Merge dei prefill DOPO l'init (sourceModule/sourceContactId
      // saranno propagati nel payload via setCaseForm)
      setCaseForm((prev) => ({
        ...prev,
        clientId: detail.clientId ? String(detail.clientId) : prev.clientId,
        subject: detail.subject || prev.subject || "",
        description: detail.description || prev.description || "",
        practiceArea: detail.practiceArea || prev.practiceArea || "",
        practiceType: detail.practiceType || prev.practiceType || "Gestionale Agenzia",
        assignedOperatorId: detail.assignedOperatorId || prev.assignedOperatorId || "",
        sourceModule: "diario",
        sourceContactId: detail.sourceContactId || "",
      }));
      setActiveTab("cases");
      setIsCaseDialogOpen(true);
    };
    window.addEventListener("gi:open-case-from-diary", onOpenCaseFromDiary);
    return () => window.removeEventListener("gi:open-case-from-diary", onOpenCaseFromDiary);
  }, [initNewCaseForm, setCaseForm, setIsCaseDialogOpen, setActiveTab]);

  // 17/07/2026 · v1.4.1 — Deep-link "Apri pratica" dalla Vista Cliente 360°.
  // Client360Dialog emette `gi:open-case-by-id` con { caseId } dopo aver
  // chiuso la sua modale; qui recuperiamo la case dallo state e apriamo
  // il CaseDialog standard (stessa UX del click su una riga del Registro).
  useEffect(() => {
    const onOpenCaseById = (ev) => {
      const detail = ev?.detail || {};
      const caseId = String(detail.caseId || "");
      if (!caseId) return;
      const target = (cases || []).find((c) => String(c.id) === caseId);
      if (!target) {
        toast.error("Pratica non trovata nel Registro corrente. Aggiorna la pagina e riprova.");
        return;
      }
      setActiveTab("cases");
      // Piccolo delay per lasciare che il tab si monti prima dell'edit.
      setTimeout(() => handleEditCase(target), 120);
    };
    window.addEventListener("gi:open-case-by-id", onOpenCaseById);
    return () => window.removeEventListener("gi:open-case-by-id", onOpenCaseById);
  }, [cases, handleEditCase, setActiveTab]);

  // 27/02/2026 — Diario → Crea Scadenza Clienti. ScadenzeTab è montata
  // solo quando la tab "scadenze" è attiva, perciò NON possiamo affidarci
  // a un singolo evento window: lo perderemmo prima del mount. Risolviamo
  // memorizzando il payload in uno stato `pendingDiaryDeadline` che
  // viene passato come prop a ScadenzeTab; ScadenzeTab lo consuma una
  // volta sola nel suo useEffect e poi lo resetta tramite callback.
  const [pendingDiaryDeadline, setPendingDiaryDeadline] = useState(null);
  // Callback stabile (useCallback) — evita che ScadenzeTab veda una
  // nuova reference per ogni render del padre, che farebbe ripartire
  // il useEffect di consume e potrebbe portare a doppia apertura del
  // dialog (bug "Crea Scadenza blocca la UI").
  const consumePendingDiaryDeadline = useCallback(() => setPendingDiaryDeadline(null), []);
  useEffect(() => {
    const onOpenDeadlineFromDiary = (ev) => {
      setPendingDiaryDeadline(ev?.detail || {});
      setActiveTab("scadenze");
    };
    window.addEventListener("gi:open-extra-deadline-from-diary", onOpenDeadlineFromDiary);
    return () => window.removeEventListener("gi:open-extra-deadline-from-diary", onOpenDeadlineFromDiary);
  }, [setActiveTab]);

  // 16/07/2026 — Deep-link cross-tab centralizzati in un hook dedicato
  // (`useDeepLinkListeners`) per snellire App.js. Comportamento
  // 1:1 rispetto agli useEffect originali.
  const { pendingEditQuoteId, consumePendingEditQuoteId } = useDeepLinkListeners({
    cases,
    setActiveTab,
    setCasesSubTab,
    handleEditCase,
  });


  // Filtered cases for the list view
  const filteredCases = useMemo(() => {
    let filtered = [...cases];

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter((c) => {
        const client = clients.find((cl) => cl.id === c.clientId);
        return (
          c.caseNumber.toLowerCase().includes(query) ||
          c.subject.toLowerCase().includes(query) ||
          c.description?.toLowerCase().includes(query) ||
          c.practiceArea?.toLowerCase().includes(query) ||
          client?.fullName.toLowerCase().includes(query)
        );
      });
    }

    if (filterStatus !== "all") {
      filtered = filtered.filter((c) => c.status === filterStatus);
    }

    if (filterPriority !== "all") {
      filtered = filtered.filter((c) => c.priority === filterPriority);
    }

    if (filterPracticeType !== "all") {
      filtered = filtered.filter((c) => c.practiceType === filterPracticeType);
    }

    // Filter by client's management type
    if (filterManagementType !== "all") {
      filtered = filtered.filter((c) => {
        const client = clients.find((cl) => cl.id === c.clientId);
        return (client?.managementType || "studio") === filterManagementType;
      });
    }

    return filtered.sort((a, b) => new Date(b.openingDate) - new Date(a.openingDate));
  }, [cases, searchQuery, filterStatus, filterPriority, filterPracticeType, filterManagementType, clients]);

  // Cases with document tracking
  const casesWithMissingDocuments = useMemo(() => {
    return activeCases.filter((c) => {
      if (!c.requestedDocuments || c.requestedDocuments.length === 0) return false;
      return c.receivedDocuments.length < c.requestedDocuments.length;
    });
  }, [activeCases]);

  const casesWithCompleteDocuments = useMemo(() => {
    return activeCases.filter((c) => {
      if (!c.requestedDocuments || c.requestedDocuments.length === 0) return false;
      return c.receivedDocuments.length === c.requestedDocuments.length;
    });
  }, [activeCases]);

  // Monthly revenue calculation
  const monthlyRevenue = useMemo(() => {
    const currentMonth = new Date().getMonth();
    const currentYear = new Date().getFullYear();
    return invoices
      .filter((inv) => {
        const invDate = new Date(inv.invoiceDate);
        return (
          inv.status === "Pagata" &&
          invDate.getMonth() === currentMonth &&
          invDate.getFullYear() === currentYear
        );
      })
      .reduce((sum, inv) => sum + inv.totale, 0);
  }, [invoices]);

  // Calculate document expiry alerts for operators and clients
  const documentExpiryAlerts = useMemo(() => {
    const alerts = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Helper per parsare data in formato DD/MM/YYYY o ISO YYYY-MM-DD.
    // Restituisce null se la stringa non è parsabile.
    const parseDate = (str) => {
      if (!str) return null;
      const parts = String(str).split('/');
      let d;
      if (parts.length === 3) {
        // DD/MM/YYYY (documento identità, formato legacy)
        d = new Date(parts[2], parts[1] - 1, parts[0]);
      } else {
        // ISO YYYY-MM-DD (visita medica, input type=date)
        d = new Date(str);
      }
      return isNaN(d.getTime()) ? null : d;
    };

    // Check operators documents (documento identità + visita medica)
    operators.forEach(op => {
      if (op.status === "sospeso") return; // Skip suspended

      // Documento identità
      const docExp = parseDate(op.documentExpiryDate);
      if (docExp) {
        const daysUntil = Math.ceil((docExp - today) / (1000 * 60 * 60 * 24));
        if (daysUntil <= 60) {
          alerts.push({
            type: 'operatore',
            subtype: 'documento',
            name: op.fullName || `${op.firstName} ${op.lastName}`,
            documentType: op.documentType || 'Documento',
            expiryDate: op.documentExpiryDate,
            daysUntil,
            status: daysUntil < 0 ? 'expired' : 'expiring',
            id: op.id
          });
        }
      }

      // 22/07/2026 · v1.10.0 — Visita medica del lavoro (D.Lgs 81/2008).
      // Warning esteso a 90gg per anticipo prenotazione (le visite hanno
      // spesso liste d'attesa da 30-60gg).
      const medExp = parseDate(op.medicalVisitExpiryDate);
      if (medExp) {
        const daysUntil = Math.ceil((medExp - today) / (1000 * 60 * 60 * 24));
        if (daysUntil <= 90) {
          alerts.push({
            type: 'operatore',
            subtype: 'visita-medica',
            name: op.fullName || `${op.firstName} ${op.lastName}`,
            documentType: 'Sorveglianza sanitaria',
            expiryDate: op.medicalVisitExpiryDate,
            daysUntil,
            status: daysUntil < 0 ? 'expired' : 'expiring',
            id: op.id
          });
        }
      }
    });

    // Check clients documents
    clients.forEach(cl => {
      const clExp = parseDate(cl.documentExpiryDate);
      if (!clExp) return;
      const daysUntil = Math.ceil((clExp - today) / (1000 * 60 * 60 * 24));
      if (daysUntil <= 60) {
        alerts.push({
          type: 'cliente',
          subtype: 'documento',
          name: cl.fullName,
          documentType: cl.documentType || 'Documento',
          expiryDate: cl.documentExpiryDate,
          daysUntil,
          status: daysUntil < 0 ? 'expired' : 'expiring',
          id: cl.id
        });
      }
    });

    // Sort by days until expiry (most urgent first)
    return alerts.sort((a, b) => a.daysUntil - b.daysUntil);
  }, [operators, clients]);

  // Document management handlers
  const toggleDocument = useCallback((caseId, document) => {
    setCases((prev) =>
      prev.map((c) => {
        if (c.id !== caseId) return c;
        const isReceived = c.receivedDocuments.includes(document);
        const updated = {
          ...c,
          receivedDocuments: isReceived
            ? c.receivedDocuments.filter((d) => d !== document)
            : [...c.receivedDocuments, document]
        };
        // Save to backend
        api.updateCase(caseId, updated).catch(console.error);
        return updated;
      })
    );
  }, [setCases]);

  const addDocumentToCase = useCallback((caseId, documentName) => {
    if (!documentName || !documentName.trim()) return;
    
    setCases((prev) =>
      prev.map((c) => {
        if (c.id !== caseId) return c;
        if (c.requestedDocuments.includes(documentName.trim())) {
          alert("Documento già presente nella lista");
          return c;
        }
        const updated = {
          ...c,
          requestedDocuments: [...c.requestedDocuments, documentName.trim()]
        };
        // Save to backend
        api.updateCase(caseId, updated).catch(console.error);
        return updated;
      })
    );
  }, [setCases]);

  const removeDocumentFromCase = useCallback((caseId, documentName) => {
    setCases((prev) =>
      prev.map((c) => {
        if (c.id !== caseId) return c;
        const updated = {
          ...c,
          requestedDocuments: c.requestedDocuments.filter((d) => d !== documentName),
          receivedDocuments: c.receivedDocuments.filter((d) => d !== documentName)
        };
        // Save to backend
        api.updateCase(caseId, updated).catch(console.error);
        return updated;
      })
    );
  }, [setCases]);

  // Operator link handler with dialog close
  const onLinkOperator = async (operatorId) => {
    try {
      await handleLinkOperator(operatorId);
      setIsOperatorLinkDialogOpen(false);
    } catch (error) {
      alert(error.message || "Errore durante il collegamento");
    }
  };

  // ─── Wave 4.2 (01/07/2026) — Stabilised callbacks ─────────────────
  // Tutte le arrow inline precedentemente passate ai <TabsContent>
  // creavano una nuova reference ad ogni render di App.js. Con i tab
  // top-level ora wrappati in React.memo (Wave 4.2), una prop
  // instabile forzerebbe comunque il re-render dell'intero subtree
  // memoizzato ad ogni switch di activeTab. Le stabilizziamo qui.
  const navigateToScadenzarioClienti = useCallback(() => {
    setScadenzeDefaultSubTab("extra");
    setActiveTab("scadenze");
  }, []);
  const goToCasesTab = useCallback(() => setActiveTab("cases"), []);
  const goToFatturazioneTab = useCallback(() => setActiveTab("fatturazione"), []);
  const goToScadenzeTab = useCallback(() => setActiveTab("scadenze"), []);
  const onCreateInvoiceFromCaseThenNavigate = useCallback((caseItem) => {
    createInvoiceFromCase(caseItem);
    setActiveTab("fatturazione");
  }, [createInvoiceFromCase]);
  const openNewCaseFromRegistry = useCallback(async () => {
    try { await initNewCaseForm(); } catch (e) { console.error("initNewCaseForm failed:", e); }
    setIsCaseDialogOpen(true);
  }, [initNewCaseForm, setIsCaseDialogOpen]);
  const consumeScadenzeSubTab = useCallback(() => setScadenzeDefaultSubTab(null), []);
  const openClientDialogSimple = useCallback(() => setIsClientDialogOpen(true), [setIsClientDialogOpen]);
  const openNewInvoiceDialog = useCallback(() => {
    resetInvoiceForm("invoice");
    setEditingInvoiceId(null);
    setIsInvoiceDialogOpen(true);
  }, [resetInvoiceForm, setEditingInvoiceId, setIsInvoiceDialogOpen]);
  // ─── Master Shell v2 · QuickBar handlers (Cruscotto) ────────────────
  const openNewPreventivoQuick = useCallback(() => {
    setActiveTab("cases");
    setCasesSubTab("preventivi");
    setPendingModulisticaForm("preventivo-pratica");
  }, []);
  const openCriticalDeadlinesQuick = useCallback(() => {
    setActiveTab("scadenze");
    setScadenzeDefaultSubTab("critiche");
  }, []);
  const openDiaryNoteQuick = useCallback(() => {
    setActiveTab("attivita");
    setActiveOperationalTab("diario");
  }, [setActiveOperationalTab]);
  const openWorkNoteQuick = useCallback(() => {
    setActiveTab("attivita");
    setActiveOperationalTab("pratiche");
  }, [setActiveOperationalTab]);
  const consumePendingModulisticaForm = useCallback(() => setPendingModulisticaForm(null), []);
  const openClientDialogFromModulistica = useCallback(() => {
    resetClientForm();
    setIsClientDialogOpen(true);
  }, [resetClientForm, setIsClientDialogOpen]);
  const clearRegistroInitialClient = useCallback(() => setRegistroInitialClient({ id: "", name: "" }), []);
  // Print Mandato Pratica PDF - uses current form data
  const handlePrintMandato = useCallback(async (caseData) => {
    if (!caseData) {
      alert("Impossibile stampare: nessun dato disponibile");
      return;
    }
    
    try {
      // If case has an ID (already saved), use the existing endpoint
      if (caseData.id) {
        await api.downloadMandatoPDF(caseData.id);
      } else {
        // Otherwise, print from current form data
        await api.printMandatoFromForm({
          clientId: caseData.clientId || "",
          practiceType: caseData.practiceType || "Gestionale Agenzia",
          practiceArea: caseData.practiceArea || "",
          subject: caseData.subject || "",
          enteCoinvolto: caseData.enteCoinvolto || "",
          openingDate: caseData.openingDate || "",
          perContoDi: caseData.perContoDi || null,
          caseNumber: caseData.caseNumber || ""
        });
      }
    } catch (error) {
      console.error("Errore stampa mandato:", error);
      alert("Errore durante la generazione del PDF: " + error.message);
    }
  }, []);

  // Loading screen
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-slate-600">Caricamento dati in corso...</p>
        </div>
      </div>
    );
  }

  // Error screen
  if (loadingError) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-50 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <p className="text-red-600 mb-4">{loadingError}</p>
          <Button onClick={loadAllData} className="bg-blue-600 hover:bg-blue-700">
            <RefreshCw className="mr-2 h-4 w-4" />
            Riprova
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="gis-shell-outer">
      <div
        className="gis-shell-inner flex flex-col"
        style={{ backgroundColor: "var(--suite-surface-alt)" }}
      >
      {/* ═════════════════════════════════════════════════════════════
          UX Evolution v3 · Fase A — 26/07/2026 · v1.11.0
          SuiteTopBar globale su TUTTI i tab: sostituisce l'AppHeader
          legacy per creare una identità unitaria della Suite.
          SuiteActionBar + SectionContextBar restano localizzate ai
          moduli che sono stati migrati (Fatturazione, prossimamente
          Cruscotto Pratiche e Attività Operative). Il vecchio
          AppHeader è deprecato — non riutilizzarlo in nuove pagine.
          ═════════════════════════════════════════════════════════════ */}
      <SuiteTopBar
        suite={{
          studio: {
            name:
              settings?.studioName ||
              settings?.companyName ||
              settings?.ragioneSociale ||
              "Gestionale Studio",
            subtitle: settings?.city || "",
            tooltip: [
              settings?.address,
              settings?.city,
              settings?.vatNumber && `P.IVA ${settings.vatNumber}`,
            ]
              .filter(Boolean)
              .join(" · "),
          },
          operator: currentOperator
            ? {
                name: currentOperator.fullName || "Operatore",
                role: currentOperator.role || "operatore",
                email: currentOperator.email || "",
                initials: (currentOperator.fullName || "??")
                  .trim()
                  .split(/\s+/)
                  .filter(Boolean)
                  .map((p) => p[0])
                  .slice(0, 2)
                  .join("")
                  .toUpperCase(),
              }
            : null,
        }}
        myWorkCount={suiteMyWorkCount}
        onOpenMyWork={() => setSuiteMyWorkOpen(true)}
        onLogout={onLogout}
      />
      {/* SuiteActionBar legacy rimossa (v1.11.0 · Master Shell v2). Azioni gestite dai singoli moduli tramite SectionContextBar / Primary Action. */}
      {/* Bridge MyWorkPanel controllato dalla SuiteTopBar (globale).
          Il trigger legacy nella pagina è nascosto. */}
      <MyWorkPanel
        currentOperator={currentOperator}
        cases={cases || []}
        setActiveTab={setActiveTab}
        hideDefaultTrigger
        open={suiteMyWorkOpen}
        onOpenChange={setSuiteMyWorkOpen}
      />

      {/* ═════════════════════════════════════════════════════════════════
          MASTER SHELL v2 · Rollout (28/07/2026 · Brand Book v1.0)
          Sidebar globale a sinistra + area operativa a destra (flex row).
          La TabsList orizzontale è nascosta: la navigazione avviene
          esclusivamente tramite la Sidebar. Tutti i deep-link, i sub-tab
          interni, i dialog e la business logic sono preservati.
          ═════════════════════════════════════════════════════════════════ */}
      <div className="gis-shell-main-row flex flex-1 min-h-0">
        <SuiteSidebar
          items={TABS_CONFIG.map((t) => ({
            value: t.value,
            label: t.label,
            icon: t.icon,
            testid: t.testid ? `sidebar-${t.testid}` : `sidebar-nav-${t.value}`,
          }))}
          activeValue={activeTab}
          onSelect={(value) => {
            if (value === "cases") setCasesSubTab("registro");
            setActiveTab(value);
          }}
        />
        <div
          ref={pageScrollRef}
          className="gis-page-scroll flex-1 min-w-0 flex flex-col"
          data-testid="gis-page-scroll"
        >

      {/* Fascia informativa Studio + Agenzia (Master Shell v2 · sempre visibile,
          compattabile temporaneamente dall'operatore). Dati letti da settings. */}
      <SuiteStudioBar settings={settings} />

      {activeTab === "dashboard" && (
        <header
          className="w-full shrink-0 bg-white px-5 sm:px-6 lg:px-8 pt-5 pb-4"
          data-testid="dashboard-page-header"
          style={{ borderBottom: "1px solid #E5E7EB" }}
        >
          <p className="gis-heading-wide text-[11px] font-semibold tracking-[0.24em] text-[var(--gis-green)] mb-1">
            Gruppo Impresa Suite
          </p>
          <h1 className="gis-heading text-[26px] sm:text-[30px] leading-tight font-bold text-slate-950">
            Gestionale di Studio
          </h1>
          <p className="gis-body mt-1 text-[13px] sm:text-sm text-slate-500">
            Pratiche, clienti, scadenze, fatturazione e attività operative
          </p>
        </header>
      )}

      {/* Barra dei comandi rapidi — SOLO Cruscotto (Master Shell v2 · Brand Book v1.0).
          7 azioni: 1 primaria verde + 6 secondarie outline. */}
      {activeTab === "dashboard" && (
        <SuiteQuickBar
          primary={{ icon: FolderPlus, label: "Nuova pratica", onClick: openNewCaseFromRegistry, testid: "quickbar-nuova-pratica" }}
          secondary={[
            { icon: UserPlus, label: "Nuovo cliente", onClick: openClientDialogSimple, testid: "quickbar-nuovo-cliente" },
            { icon: ReceiptText, label: "Nuova fattura", onClick: openNewInvoiceDialog, testid: "quickbar-nuova-fattura" },
            { icon: FileSignature, label: "Nuovo preventivo", onClick: openNewPreventivoQuick, testid: "quickbar-nuovo-preventivo" },
            { icon: AlertTriangle, label: "Scadenze critiche", onClick: openCriticalDeadlinesQuick, testid: "quickbar-scadenze-critiche" },
            { icon: BookUser, label: "Nota diario", onClick: openDiaryNoteQuick, testid: "quickbar-nota-diario" },
            { icon: ClipboardList, label: "Nota lavoro", onClick: openWorkNoteQuick, testid: "quickbar-nota-lavoro" },
          ]}
        />
      )}

      <div className="flex-1 w-full">
      <div className="container mx-auto px-3 sm:px-4 lg:px-6 py-6 max-w-[1600px] w-full">
        {/* Operator Warning Banners */}
        {!currentOperator && operators.length > 0 && (
          <OperatorWarningBanner 
            operators={operators}
            setIsOperatorLinkDialogOpen={setIsOperatorLinkDialogOpen}
          />
        )}
        {!currentOperator && operators.length === 0 && <NoOperatorsInfoBanner />}

        {/* Header with Action Buttons — DEPRECATO (v1.11.0 · UX v3)
            AppHeader legacy è sostituito dalla SuiteTopBar globale.
            Il blocco resta commentato per riferimento ma NON viene più renderizzato.
            I 6 quick-buttons dei dialog sono ancora presenti sotto (in modalità
            hidden) per lasciare che i Dialog stessi restino montati in App. */}
        <div className="hidden">
          <div className="flex flex-col gap-1.5 mb-1.5">
            <AppHeader
              currentOperator={currentOperator}
              isSaving={isSaving}
              loadAllData={loadAllData}
              onLogout={onLogout}
              settings={settings}
            />
            
            {/* Action Buttons - Centered (nascosti globalmente in v1.11.0) */}
            <div className="flex justify-center gap-3 flex-wrap">
              {/* Case Dialog - Primary Action */}
              <CaseDialog
                isOpen={isCaseDialogOpen}
                onOpenChange={setIsCaseDialogOpen}
                editingCase={editingCase}
                caseForm={caseForm}
                setCaseForm={setCaseForm}
                onSave={handleSaveCase}
                onReset={resetCaseForm}
                onInitNew={initNewCaseForm}
                onAfterSave={() => setActiveTab("cases")}
                onSaveAndInvoice={(createdCase) => {
                  if (createdCase) {
                    createInvoiceFromCase(createdCase);
                  }
                  setActiveTab("fatturazione");
                }}
                clients={clients}
                operators={operators}
                cases={cases}
                getOperatorName={getOperatorName}
                generatePreviewCaseNumberSync={generatePreviewCaseNumberSync}
                fetchNextCaseNumber={fetchNextCaseNumber}
                isSaving={isSaving}
                onOpenClientDialog={() => {
                  resetClientForm();
                  setClientDialogFromCase(true);
                  setIsClientDialogOpen(true);
                }}
                currentOperator={currentOperator}
                onPrintMandato={handlePrintMandato}
                caseHistory={caseHistory}
                configTables={configTables}
              />

              {/* Client Dialog - Secondary Action */}
              <ClientDialog
                isOpen={isClientDialogOpen}
                onOpenChange={(open) => {
                  setIsClientDialogOpen(open);
                  if (!open) {
                    setClientDialogFromCase(false);
                    setClientDialogFromInvoice(false);
                  }
                }}
                editingClient={editingClient}
                clientForm={clientForm}
                setClientForm={setClientForm}
                onSave={handleSaveClientWithAutoSelect}
                onReset={() => {
                  resetClientForm();
                  setClientDialogFromCase(false);
                  setClientDialogFromInvoice(false);
                }}
                isSaving={isSaving}
              />

              {/* Invoice Dialog - Tertiary Action */}
              <InvoiceDialog
                isOpen={isInvoiceDialogOpen}
                onOpenChange={setIsInvoiceDialogOpen}
                invoiceForm={invoiceForm}
                setInvoiceForm={setInvoiceForm}
                onSave={handleSaveInvoice}
                onPreview={handlePreviewInvoice}
                onReset={resetInvoiceForm}
                clients={clients}
                cases={cases}
                invoices={invoices}
                isSaving={isSaving}
                isPreviewLoading={isPreviewLoading}
                isEditing={!!editingInvoiceId}
                settings={settings}
                saveError={saveError}
                clearSaveError={clearSaveError}
                onCreateClient={(prefilledName) => {
                  resetClientForm();
                  if (prefilledName) {
                    // Pre-fill all name-like fields so the search text appears
                    // regardless of which clientType the user keeps/picks
                    // (Privato uses lastName, Ditta individuale uses both,
                    // Azienda uses fullName).
                    setClientForm(prev => ({
                      ...prev,
                      fullName: prefilledName,
                      lastName: prefilledName,
                    }));
                  }
                  setClientDialogFromInvoice(true);
                  setIsClientDialogOpen(true);
                }}
              />
              
              {/* Critical Deadlines Button - includes document expiries */}
              {(criticalDeadlines.length > 0 || documentExpiryAlerts.length > 0) && (
                <Button
                  variant="outline"
                  className="border-red-300 text-red-700 hover:bg-red-100 hover:border-red-400 hover:scale-105 shadow-sm transition-all duration-200"
                  onClick={() => setActiveTab("scadenze")}
                  data-testid="critical-deadlines-button"
                >
                  <AlertTriangle className="mr-2 h-4 w-4 animate-pulse" />
                  Scadenze Critiche ({criticalDeadlines.length + documentExpiryAlerts.length})
                </Button>
              )}

              {/* Operational quick-access: Diario Giornaliero
                  → apre direttamente il flow "Nuova Nota". */}
              <Button
                variant="outline"
                size="sm"
                onClick={openDiarioTab}
                data-testid="quick-action-diario"
                title="Nuova nota nel Diario Giornaliero"
                className="border-violet-300 text-violet-700 hover:bg-violet-100 hover:border-violet-400 hover:scale-105 shadow-sm transition-all duration-200"
              >
                <BookUser className="mr-2 h-4 w-4" />
                Nota Diario
              </Button>

              {/* Operational quick-access: Lavoro Quotidiano (ex Pratiche
                  Quotidiane). Apre direttamente il flow "Nuovo Lavoro". */}
              <Button
                variant="outline"
                size="sm"
                onClick={openLavoroQuotidianoTab}
                data-testid="quick-action-lavoro-quotidiano"
                title="Nuovo lavoro in Lavoro Quotidiano"
                className="border-cyan-300 text-cyan-700 hover:bg-cyan-100 hover:border-cyan-400 hover:scale-105 shadow-sm transition-all duration-200"
              >
                <ClipboardList className="mr-2 h-4 w-4" />
                Nota Lavoro
              </Button>

              {/* Quick action: "+ Nuovo Preventivo" — ricollocato
                  qui (28/02/2026) accanto a "Lavoro Quotidiano" per
                  uniformità visiva. Stile outline delicato (emerald)
                  in linea con gli altri quick-access. */}
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  // 14/07/2026 — Reorg Gestionale Pratiche: il flusso
                  // Nuovo Preventivo è passato da Modulistica al
                  // sub-tab "Preventivi" di Gestionale Pratiche. Setto
                  // sub-tab + il form-template attivo, poi navigo.
                  setCasesSubTab("preventivi");
                  setPendingModulisticaForm("preventivo-pratica");
                  setActiveTab("cases");
                }}
                data-testid="quick-new-preventivo-btn"
                title="Apri il flusso 'Nuovo Preventivo' in Gestione Pratiche → Preventivi"
                className="gis-border-green gis-text-green hover:bg-[var(--gis-green-50)] hover:scale-105 shadow-sm transition-all duration-200"
              >
                <FileSignature className="mr-2 h-4 w-4" />
                Nuovo Preventivo
              </Button>

              {/* Operator Link Dialog */}
              <OperatorLinkDialog
                isOpen={isOperatorLinkDialogOpen}
                onOpenChange={setIsOperatorLinkDialogOpen}
                operators={operators}
                onLinkOperator={onLinkOperator}
              />
            </div>
          </div>
        </div>

        {/* Main Content Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-3">
          {/* "Il mio lavoro" — ancorato qui (22/02/2026, refinement UI):
              SOPRA al menu di navigazione, sempre visibile in tutte le
              sezioni dell'app. v1.8.0 · UX Operativa v2: compattato,
              rimossa la riga di istruzione "Usa il pulsante..." per
              recuperare ~30px verticali. Il pulsante è auto-esplicativo.

              v1.11.0 · UX v3 · Fase A: il chip esterno è NASCOSTO
              globalmente. Il pulsante "Il mio lavoro" è ora sempre
              accessibile via icona dedicata nella SuiteTopBar. */}
          <div
            className="hidden flex items-center justify-end gap-3 my-work-anchor"
            data-testid="my-work-anchor"
          >
            <div className="flex items-center gap-2">
              <MyWorkPanel
                currentOperator={currentOperator}
                cases={cases || []}
                setActiveTab={setActiveTab}
                prominent
              />
            </div>
          </div>
          {/* Menu orizzontale legacy rimosso in Master Shell v2 (v1.11.0).
              La navigazione principale avviene esclusivamente tramite <SuiteSidebar>. */}

          {/* Dashboard Tab */}
          <TabsContent value="dashboard" data-testid="dashboard-content" className="space-y-4" {...tabMountProp("dashboard")}>
            <DashboardTab
              clients={clients}
              cases={cases}
              invoices={invoices}
              deadlines={deadlines}
              activeCases={activeCases}
              criticalDeadlines={criticalDeadlines}
              upcomingDeadlines={upcomingDeadlines}
              unpaidInvoices={unpaidInvoices}
              casesToBill={casesToBill}
              totalToBill={totalToBill}
              totalUnpaid={totalUnpaid}
              setActiveTab={setActiveTab}
              navigateToCasesWithStatus={navigateToCasesWithStatus}
              toggleDeadlineStatus={toggleDeadlineStatus}
              updateInvoiceStatus={updateInvoiceStatus}
              getDeadlineUrgency={getDeadlineUrgency}
              operators={operators}
              currentOperator={currentOperator}
              operatorRole={operatorRole}
              documentExpiryAlerts={documentExpiryAlerts}
              navigateToScadenzarioClienti={navigateToScadenzarioClienti}
            />
          </TabsContent>

          {/* Clients Tab */}
          <TabsContent value="clients" data-testid="clients-content" className="space-y-4" {...tabMountProp("clients")}>
            <ClientiTab
              clients={clients}
              cases={cases}
              handleEditClient={handleEditClient}
              handleDeleteClient={handleDeleteClient}
              onNewClient={openClientDialogSimple}
              onOpenClient360={() => setActiveTab("cliente360")}
              onOpenRegistroAttivita={openRegistroForClient}
              onReloadClients={async () => {
                try { setClients(await api.getClients()); } catch { /* silent */ }
              }}
            />
          </TabsContent>

          {/* Cases Tab */}
          {/* Gestionale Pratiche — modulo unico (11/06/2026, riorganiz-
              zazione UI/UX su richiesta utente).
              Aggiornato 14/07/2026: aggiunto sub-tab "Preventivi" come
              prima voce visiva. Il default resta "registro" (Registro
              Pratiche) — è la vista operativa di apertura.
              Contiene 4 sub-tab interni che raggruppano TUTTA
              l'operatività centrata sulle pratiche:
                · Preventivi            (Modulistica restrictToPreventivi)
                · Registro Pratiche     (PraticheTab)
                · Note su Pratiche      (NoteInterneTab, ex Note Pratica)
                · Documenti Mancanti    (DocumentiTab, ex Documenti)
              I `value` dei tab principali (cases) e l'API dei componenti
              sottostanti restano IDENTICI: cambia solo la gerarchia
              visiva. Nessun impatto su routing, permessi, business logic. */}
          <TabsContent value="cases" data-testid="cases-content" className="space-y-4" {...tabMountProp("cases")}>
            {(() => {
              // v1.11.0 · UX v3 · Cruscotto Pratiche — KPI cliccabili
              const CLOSED = new Set(["Chiusa", "Archiviata", "chiusa", "archiviata"]);
              const arr = Array.isArray(cases) ? cases : [];
              const aperte = arr.filter((c) => !CLOSED.has(c.status));
              const inLavorazione = arr.filter((c) =>
                ["In lavorazione", "in lavorazione", "Lavorazione"].includes(c.status)
              );
              const inAttesa = arr.filter((c) =>
                ["In attesa", "in attesa", "Attesa"].includes(c.status)
              );
              const daDocumenti = arr.filter(
                (c) => Array.isArray(c.missingDocuments) && c.missingDocuments.length > 0
              );
              const contextKpis = [
                {
                  value: aperte.length,
                  label: "pratiche aperte",
                  tone: "default",
                  testid: "cases-ctx-kpi-aperte",
                  onClick: () => setCasesSubTab("registro"),
                },
                {
                  value: inLavorazione.length,
                  label: "in lavorazione",
                  tone: inLavorazione.length > 0 ? "info" : "default",
                  testid: "cases-ctx-kpi-lavorazione",
                  onClick: () => setCasesSubTab("registro"),
                },
                {
                  value: inAttesa.length,
                  label: "in attesa",
                  tone: inAttesa.length > 0 ? "warning" : "default",
                  testid: "cases-ctx-kpi-attesa",
                  onClick: () => setCasesSubTab("registro"),
                },
                {
                  value: daDocumenti.length,
                  label: "documenti mancanti",
                  tone: daDocumenti.length > 0 ? "danger" : "default",
                  testid: "cases-ctx-kpi-docs",
                  onClick: () => setCasesSubTab("documenti"),
                },
              ];
              const critical = daDocumenti.length > 0
                ? { message: `${daDocumenti.length} pratiche con documenti mancanti`, onClick: () => setCasesSubTab("documenti") }
                : null;
              return (
                <SectionContextBar
                  icon={FolderKanban}
                  title="Gestione Pratiche"
                  description="Preventivi, registro pratiche, note e documenti"
                  kpis={contextKpis}
                  criticalNotice={critical}
                  testid="cases-context-bar"
                />
              );
            })()}
            <Tabs
              value={casesSubTab}
              onValueChange={setCasesSubTab}
              className="w-full"
            >
              {/* Sub-nav flat coerente con Fatturazione (UX v3) */}
              <TabsList
                className="suite-subnav w-auto h-auto p-0 bg-transparent rounded-none gap-0 justify-start inline-flex"
                style={{ borderBottom: "1px solid var(--suite-border)" }}
              >
                <TabsTrigger
                  value="preventivi"
                  data-testid="cases-subtab-preventivi"
                  className="suite-subnav-item data-[state=active]:bg-transparent data-[state=active]:text-[var(--gis-green)] data-[state=active]:shadow-none rounded-none border-0 relative"
                >
                  <FileSignature className="h-3.5 w-3.5 mr-1.5" />
                  Preventivi
                </TabsTrigger>
                <TabsTrigger
                  value="registro"
                  data-testid="cases-subtab-registro"
                  className="suite-subnav-item data-[state=active]:bg-transparent data-[state=active]:text-[var(--gis-green)] data-[state=active]:shadow-none rounded-none border-0 relative"
                >
                  <FileText className="h-3.5 w-3.5 mr-1.5" />
                  Registro Pratiche
                </TabsTrigger>
                <TabsTrigger
                  value="note-pratica"
                  data-testid="cases-subtab-note"
                  className="suite-subnav-item data-[state=active]:bg-transparent data-[state=active]:text-[var(--gis-green)] data-[state=active]:shadow-none rounded-none border-0 relative"
                >
                  <MessageSquare className="h-3.5 w-3.5 mr-1.5" />
                  Note su Pratiche
                </TabsTrigger>
                <TabsTrigger
                  value="documenti"
                  data-testid="cases-subtab-documenti"
                  className="suite-subnav-item data-[state=active]:bg-transparent data-[state=active]:text-[var(--gis-green)] data-[state=active]:shadow-none rounded-none border-0 relative"
                >
                  <FileCheck className="h-3.5 w-3.5 mr-1.5" />
                  Documenti Mancanti
                </TabsTrigger>
              </TabsList>

              {/* 14/07/2026 — Sub-tab "Preventivi" spostato qui da
                  Modulistica. Riusa lo STESSO componente `Modulistica`
                  in modalità dedicata (prop `restrictToPreventivi`),
                  quindi nessuna duplicazione di codice o divergenza
                  funzionale sui form del preventivo. Convertito in
                  Pratica, PDF, versioning, numerazione: tutto identico. */}
              <TabsContent value="preventivi" data-testid="preventivi-content" className="space-y-4 mt-4">
                <Modulistica
                  restrictToPreventivi={true}
                  clients={clients}
                  operators={operators}
                  currentOperator={currentOperator}
                  cases={cases}
                  configTables={configTables}
                  initialFormType={pendingModulisticaForm}
                  onInitialFormConsumed={consumePendingModulisticaForm}
                  initialEditFormId={pendingEditQuoteId}
                  onInitialEditFormConsumed={consumePendingEditQuoteId}
                  onOpenClientDialog={openClientDialogFromModulistica}
                />
              </TabsContent>

              <TabsContent value="registro" className="space-y-4 mt-4">
                <PraticheTab
                  cases={cases}
                  setCases={setCases}
                  clients={clients}
                  deadlines={deadlines}
                  operators={operators}
                  filteredCases={filteredCases}
                  searchQuery={searchQuery}
                  setSearchQuery={setSearchQuery}
                  filterStatus={filterStatus}
                  setFilterStatus={setFilterStatus}
                  filterPriority={filterPriority}
                  setFilterPriority={setFilterPriority}
                  filterPracticeType={filterPracticeType}
                  setFilterPracticeType={setFilterPracticeType}
                  filterManagementType={filterManagementType}
                  setFilterManagementType={setFilterManagementType}
                  handleEditCase={handleEditCase}
                  handleDeleteCase={handleDeleteCase}
                  handleQuickStatusChange={handleQuickStatusChange}
                  getOperatorName={getOperatorName}
                  currentOperator={currentOperator}
                  onCreateInvoiceFromCase={onCreateInvoiceFromCaseThenNavigate}
                  // 29/06/2026 — "Nuova Pratica" inline dal Registro
                  // Pratiche: stesso flow del Cruscotto. Init form +
                  // apre CaseDialog. Post-save il list refresh è
                  // automatico (lo stato `cases` viene aggiornato da
                  // useGestionaleData e PraticheTab riceve la nuova prop).
                  onNewCase={openNewCaseFromRegistry}
                />
              </TabsContent>

              <TabsContent
                value="note-pratica"
                data-testid="note-pratica-content"
                className="space-y-4 mt-4"
              >
                <NoteInterneTab
                  cases={cases}
                  setCases={setCases}
                  clients={clients}
                  operators={operators}
                  getOperatorName={getOperatorName}
                  currentOperator={currentOperator}
                  operatorRole={operatorRole}
                />
              </TabsContent>

              <TabsContent
                value="documenti"
                data-testid="documents-content"
                className="space-y-4 mt-4"
              >
                <DocumentiTab
                  clients={clients}
                  cases={cases}
                  activeCases={activeCases}
                  searchQuery={searchQuery}
                  setSearchQuery={setSearchQuery}
                  filterClient={filterClient}
                  setFilterClient={setFilterClient}
                  filterDocumentStatus={filterDocumentStatus}
                  setFilterDocumentStatus={setFilterDocumentStatus}
                  toggleDocument={toggleDocument}
                  addDocumentToCase={addDocumentToCase}
                  removeDocumentFromCase={removeDocumentFromCase}
                  documentTemplates={documentTemplates}
                />
              </TabsContent>
            </Tabs>
          </TabsContent>

          {/* Deadlines Tab */}
          <TabsContent value="scadenze" data-testid="deadlines-content" className="space-y-4" {...tabMountProp("scadenze")}>
            <ScadenzeTab
              pendingDiaryDeadline={pendingDiaryDeadline}
              onPendingDiaryDeadlineConsumed={consumePendingDiaryDeadline}
              deadlines={deadlines}
              cases={cases}
              clients={clients}
              operators={operators}
              activeCases={activeCases}
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              filterClient={filterClient}
              setFilterClient={setFilterClient}
              isDeadlineDialogOpen={isDeadlineDialogOpen}
              setIsDeadlineDialogOpen={setIsDeadlineDialogOpen}
              deadlineForm={deadlineForm}
              setDeadlineForm={setDeadlineForm}
              editingDeadline={editingDeadline}
              resetDeadlineForm={resetDeadlineForm}
              handleSaveDeadline={handleSaveDeadline}
              toggleDeadlineStatus={toggleDeadlineStatus}
              handleDeleteDeadline={handleDeleteDeadline}
              getDeadlineUrgency={getDeadlineUrgency}
              getOperatorName={getOperatorName}
              operatorRole={operatorRole}
              defaultSubTab={scadenzeDefaultSubTab}
              onSubTabConsumed={consumeScadenzeSubTab}
              onOpenNewClient={openClientDialogSimple}
              isClientDialogOpen={isClientDialogOpen}
              clientForm={clientForm}
              setClientForm={setClientForm}
              handleSaveClient={handleSaveClient}
              setIsClientDialogOpen={setIsClientDialogOpen}
              resetClientForm={resetClientForm}
            />
          </TabsContent>

          {/* Documents Tab — RIMOSSO 11/06/2026: spostato come
              sub-tab "Documenti Mancanti" dentro <TabsContent value="cases">
              (vedi sopra). Routing principale non più esposto in
              TabsList; nessun setActiveTab interno usava "documents". */}

          {/* Fatturazione Tab (all users, sub-tab access controlled by role) */}
          <TabsContent value="fatturazione" data-testid="fatturazione-content" className="space-y-4" {...tabMountProp("fatturazione")}>
            <FatturazioneTab
              clients={clients}
              casesToBill={casesToBill}
              readyToBill={readyToBill}
              missingBillingData={missingBillingData}
              totalToBill={totalToBill}
              billingSearchQuery={searchQuery}
              setBillingSearchQuery={setSearchQuery}
              billingFilterClient={filterClient}
              setBillingFilterClient={setFilterClient}
              billingFilterPriority={filterPriority}
              setBillingFilterPriority={setFilterPriority}
              billingFilterStatus={filterBillingStatus}
              setBillingFilterStatus={setFilterBillingStatus}
              handleEditCase={handleEditCase}
              createInvoiceFromCase={createInvoiceFromCase}
              invoices={invoices}
              cases={cases}
              updateInvoiceStatus={updateInvoiceStatus}
              exportInvoiceXML={exportInvoiceXML}
              onAddPayment={addInvoicePayment}
              onRemovePayment={removeInvoicePayment}
              refreshInvoices={refreshInvoices}
              onEditInvoice={editInvoice}
              onDuplicateInvoice={duplicateInvoice}
              onCreateInvoice={openNewInvoiceDialog}
              onDeleteInvoice={deleteInvoice}
              currentOperator={currentOperator}
              operatorRole={operatorRole}
              settings={settings}
              openNewProforma={openNewProforma}
              operators={operators}
              onOpenNewClient={openClientDialogSimple}
              isClientDialogOpen={isClientDialogOpen}
              setIsClientDialogOpen={setIsClientDialogOpen}
              setClientForm={setClientForm}
              resetClientForm={resetClientForm}
            />
          </TabsContent>

          {/* Modulistica Tab */}
          <TabsContent value="modulistica" data-testid="modulistica-content" className="space-y-6" {...tabMountProp("modulistica")}>
            <Modulistica 
              clients={clients} 
              operators={operators}
              cases={cases}
              currentOperator={currentOperator}
              initialFormType={pendingModulisticaForm}
              onInitialFormConsumed={consumePendingModulisticaForm}
              configTables={configTables}
              onOpenClientDialog={openClientDialogFromModulistica}
            />
          </TabsContent>

          {/* Note Pratica Tab - Centralized view of all notes */}
          {/* Note Pratica Tab — RIMOSSO 11/06/2026: spostato come
              sub-tab "Note su Pratiche" dentro <TabsContent value="cases">
              (vedi sopra). Nessun setActiveTab interno usava
              "note-pratica". */}

          {/* Attività Operative — parent tab con 2 sub-tab:
              "Diario Giornaliero" (esistente) + "Lavoro Quotidiano"
              (ex "Pratiche Quotidiane").
              22/02/2026: rimossi i Radix `<TabsContent>` interni che
              causavano unmount/mount pesante ad ogni switch e race
              `insertBefore` / `removeChild` con i portali Radix
              Select + NotificationBell. Sostituiti con render
              condizionale puro: SOLO IL SUB-TAB ATTIVO è montato nel
              DOM. `key={subtab}` garantisce un mount pulito e isolato
              quando si cambia tab. `SafeBoundary` resta come safety
              net per crash residui. */}
          {/* Attività Operative content è renderizzato FUORI dal
              <Tabs> (vedi sibling dopo </Tabs>), così Tabs/Provider/
              Presence di Radix non appaiono nel component stack di
              PraticheQuotidianeTab / RubricaClientiTab. */}

          {/* Impostazioni Tab - includes Dati Azienda and Operatori (admin only) */}
          {isAdmin && (
          <TabsContent value="impostazioni" data-testid="impostazioni-content" className="space-y-6" {...tabMountProp("impostazioni")}>
            <ImpostazioniCombinate 
              operators={operators} 
              setOperators={setOperators} 
              onRefresh={loadAllData}
              operatorRole={operatorRole}
              configTables={configTables}
              refreshConfigTables={refreshConfigTables}
            />
          </TabsContent>
          )}

          {/* Report Tab — accessibile a tutti i ruoli (27/02/2026) */}
          <TabsContent value="report" data-testid="report-content" className="space-y-4" {...tabMountProp("report")}>
            <ReportTab clients={clients} isAdmin={isAdmin} />
          </TabsContent>

          {/* Registro Attività Tab — vista aggregata read-only su
              Lavoro Quotidiano + Diario Giornaliero + Scadenze.
              Accessibile a tutti i ruoli (correzione perimetro
              permessi 20/02/2026). Per operatori Studio/Agenzia il
              backend forza il filtro managementType compatibile col
              loro ruolo, quindi non vedono dati operativi di area
              estranea. */}
          <TabsContent value="registro-attivita" data-testid="registro-attivita-content" className="space-y-4" {...tabMountProp("registro-attivita")}>
            <RegistroAttivitaTab
              clients={clients}
              operators={operators}
              operatorRole={operatorRole}
              initialClientId={registroInitialClient.id}
              initialClientName={registroInitialClient.name}
              onClearInitialClient={clearRegistroInitialClient}
            />
          </TabsContent>
        </Tabs>

        {/* ─────────────────────────────────────────────────────────────
            Attività Operative — RENDERIZZATO COME SIBLING DI `<Tabs>`,
            NON DENTRO. Questa è la differenza chiave rispetto al
            tentativo precedente: spostando il blocco FUORI dal
            `<Tabs>` parent di Radix, il component stack di
            PraticheQuotidianeTab / RubricaClientiTab NON contiene
            più `Tabs`, `Provider`, `Presence`, `Primitive.div` di
            Radix. La navigazione resta controllata da `activeTab`
            (impostato da `<TabsTrigger value="attivita">`), ma il
            rendering avviene in un sotto-albero React isolato dal
            Provider di Radix Tabs (che usa context + useLayoutEffect
            internal DOM mutations potenzialmente in race con la
            riconciliazione dei subtab pesanti).
            ─────────────────────────────────────────────────────────── */}
        {visitedTabs.has("attivita") && (
          <div
            data-testid="attivita-content"
            className="space-y-4 mt-6"
            hidden={activeTab !== "attivita"}
            style={activeTab !== "attivita" ? { display: "none" } : undefined}
          >
            {(() => {
              // v1.11.0 · UX v3 · Attività Operative — KPI cliccabili
              const CLOSED = new Set(["Chiusa", "Archiviata", "chiusa", "archiviata"]);
              const arr = Array.isArray(cases) ? cases : [];
              const open = arr.filter((c) => !CLOSED.has(c.status));
              const unassigned = open.filter(
                (c) => !c.assignedOperatorId && !(Array.isArray(c.assignedOperatorIds) && c.assignedOperatorIds.length)
              );
              // "In ritardo" = pratiche aperte con scadenza già passata
              const todayNow = new Date();
              const inRitardo = open.filter((c) => {
                const dueRaw = c.dueDate || c.expiryDate || c.scadenza || c.deadlineDate;
                if (!dueRaw) return false;
                const d = new Date(dueRaw);
                if (Number.isNaN(d.getTime())) return false;
                return d < todayNow;
              });
              const today = todayNow.toISOString().slice(0, 10);
              const closedToday = arr.filter((c) => {
                if (!CLOSED.has(c.status)) return false;
                const d = String(c.closedAt || c.updatedAt || "").slice(0, 10);
                return d === today;
              });
              const contextKpis = [
                { value: open.length, label: "attività aperte", tone: "default", testid: "attivita-ctx-kpi-open", onClick: () => setActiveOperationalTab("pratiche") },
                { value: unassigned.length, label: "da assegnare", tone: unassigned.length > 0 ? "warning" : "default", testid: "attivita-ctx-kpi-unassigned", onClick: () => setActiveOperationalTab("pratiche") },
                { value: inRitardo.length, label: "in ritardo", tone: inRitardo.length > 0 ? "danger" : "default", testid: "attivita-ctx-kpi-ritardo", onClick: () => setActiveOperationalTab("pratiche") },
                { value: closedToday.length, label: "completate oggi", tone: closedToday.length > 0 ? "success" : "default", testid: "attivita-ctx-kpi-done-today", onClick: () => setActiveOperationalTab("pratiche") },
              ];
              const critical = inRitardo.length > 0
                ? { message: `${inRitardo.length} attività in ritardo`, onClick: () => setActiveOperationalTab("pratiche") }
                : null;
              return (
                <SectionContextBar
                  icon={ClipboardCheck}
                  title="Attività Operative"
                  description="Diario giornaliero e lavoro quotidiano sulle pratiche"
                  kpis={contextKpis}
                  criticalNotice={critical}
                  testid="attivita-context-bar"
                />
              );
            })()}
            <div className="w-full">
              <div className="flex items-center gap-2 mb-3 flex-wrap">
                {/* Sub-nav flat Suite v3 (26/07/2026) — sostituisce i due
                    bottoni custom con lo stesso pattern chip di Fatturazione
                    e Cruscotto Pratiche. */}
                <div
                  role="tablist"
                  className="suite-subnav w-auto h-auto p-0 bg-transparent gap-0 inline-flex"
                  style={{ borderBottom: "1px solid var(--suite-border)" }}
                >
                  <button
                    role="tab"
                    aria-selected={activeOperationalTab === "diario"}
                    data-state={activeOperationalTab === "diario" ? "active" : "inactive"}
                    onClick={() => setActiveOperationalTab("diario")}
                    className="suite-subnav-item"
                    data-testid="attivita-subtab-diario"
                    type="button"
                  >
                    <BookUser className="h-3.5 w-3.5 mr-1.5" />
                    Diario Giornaliero
                  </button>
                  <button
                    role="tab"
                    aria-selected={activeOperationalTab === "pratiche"}
                    data-state={activeOperationalTab === "pratiche" ? "active" : "inactive"}
                    onClick={() => setActiveOperationalTab("pratiche")}
                    className="suite-subnav-item"
                    data-testid="attivita-subtab-pratiche"
                    type="button"
                  >
                    <ClipboardList className="h-3.5 w-3.5 mr-1.5" />
                    Lavoro Quotidiano
                  </button>
                </div>
              </div>
              {activeOperationalTab === "diario" && (
                <SafeBoundary label="DiarioGiornaliero" key={safeMode ? "diario-safe" : "diario-subtab"}>
                  {safeMode ? <SafeModeDiarioGiornaliero /> : (
                    <RubricaClientiTab
                      clients={clients}
                      cases={cases}
                      operators={operators}
                      currentOperator={currentOperator}
                      getOperatorName={getOperatorName}
                      operatorRole={operatorRole}
                      onOpenNewClient={openNewClientDialog}
                      isClientDialogOpen={isClientDialogOpen}
                      setIsClientDialogOpen={setIsClientDialogOpen}
                      clientForm={clientForm}
                      setClientForm={setClientForm}
                      resetClientForm={resetClientForm}
                      pendingQuickAction={pendingQuickAction}
                      consumeQuickAction={consumeQuickAction}
                    />
                  )}
                </SafeBoundary>
              )}
              {activeOperationalTab === "pratiche" && (
                <SafeBoundary label="LavoroQuotidiano" key={safeMode ? "pratiche-safe" : "pratiche-subtab"}>
                  {safeMode ? <SafeModeLavoroQuotidiano /> : (
                    <PraticheQuotidianeTab
                      clients={clients}
                      operators={operators}
                      currentOperator={currentOperator}
                      operatorRole={operatorRole}
                      onOpenNewClient={openNewClientDialog}
                      isClientDialogOpen={isClientDialogOpen}
                      setClientForm={setClientForm}
                      resetClientForm={resetClientForm}
                      pendingQuickAction={pendingQuickAction}
                      consumeQuickAction={consumeQuickAction}
                    />
                  )}
                </SafeBoundary>
              )}
            </div>
          </div>
        )}
      </div>
      </div>
        </div>
      </div>
      
      {/* PDF Preview Modal - In-app preview to avoid browser popup blocks */}
      <PDFPreviewModal
        isOpen={isPreviewModalOpen}
        onClose={closePreviewModal}
        pdfBlob={previewPdfBlob}
        isLoading={isPreviewLoading}
        error={previewError}
        title="Anteprima Fattura"
        filename={previewFilename}
      />

      {/* Footer tecnico release — 22/02/2026.
          Estende alla vista autenticata lo stesso footer già presente
          in login. Fonte unica: `APP_RELEASE` in
          `/app/frontend/src/config/appRelease.js`. Layout:
          - non fixed: scorre in coda ai contenuti lunghi;
          - `flex-col + flex-1` sul wrapper garantisce pin in fondo su
            contenuti corti;
          - linea superiore sottile, sfondo neutro, no glass-morphism. */}
      <footer
        className="w-full shrink-0 border-t border-slate-200 bg-white/90 px-4 py-2 text-[10px] leading-tight text-slate-600 sm:px-6 sm:text-[11px]"
        data-testid="app-tech-footer"
      >
        <div className="flex flex-col items-center justify-between gap-1 text-center xl:flex-row xl:gap-6 xl:text-left">
          <div
            className="whitespace-normal sm:whitespace-nowrap"
            data-testid="app-tech-footer-app-info"
          >
            Gestionale di Studio · Portale operativo · v{APP_RELEASE.version} · Ultimo aggiornamento {APP_RELEASE.updatedAt}
          </div>
          <div
            className="whitespace-normal sm:whitespace-nowrap xl:text-right"
            data-testid="app-tech-footer-copyright"
          >
            © 2026 Gruppo Impresa S.r.l. S.T.P. · C.F./P.IVA 15948751001 · Tutti i diritti riservati.
          </div>
        </div>
      </footer>

      </div>
    </div>
  );
}
